import { Point } from "appworks/utils/geom/point";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { DataProcessorSupplement } from "slotworks/model/gameplay/supplements/data-processor-supplement";
import { slotDefinition } from "slotworks/model/slot-definition";
import { SymbolDefinition, SymbolMatchType } from "slotworks/model/symbol-definition";
import { AnticipationResult } from "../records/results/anticipation-result";

interface IScatterAnticipationTracker {
    reelCounts: number[];
    requiredSymbolsCounts: number[];
    triggerCounts: Map<string, number[]>;
    anticipation: Map<string, number>;
    positions: Point[];
    possibleMatchesPerReel: number;
    requiredSymbolIds: number[];
    numRequiredSymbols: number;
}

interface ILineAnticipationTracker {
    lineCounts: number[][];
    requiredSymbolsCounts: number[][];
    triggerCounts: Map<string, number[]>;
    anticipation: Map<string, number>;
    positions: Point[];
    possibleMatchesPerReel: number;
    requiredSymbolIds: number[];
    numRequiredSymbols: number;
}

interface IWayAnticipationTracker {
    reelCounts: number[];
    requiredSymbolsCounts: number[];
    triggerCounts: Map<string, number[]>;
    anticipation: Map<string, number>;
    positions: Point[];
    possibleMatchesPerReel: number;
    requiredSymbolIds: number[];
    numRequiredSymbols: number;
}

export class AnticipationSupplement implements DataProcessorSupplement {
    public reelOrder?: number[];

    public active: boolean = true;
    // Disable supplement for these record types
    public excludeRecordTypes: Array<{ new(...args: any[]): any }>;
    protected reels: string[][];
    protected selectedReelOrder: number[];

    constructor(reelOrder?: number[]) {
        this.reelOrder = reelOrder;
    }

    public process(record: SpinRecord) {
        const definitionsExist = !!slotDefinition.symbolDefinitions;
        const recordHasGrid = record.grid;

        if (!this.active || !definitionsExist || !recordHasGrid) {
            return;
        }

        if (this.excludeRecordTypes) {
            let excluded = false;
            this.excludeRecordTypes.forEach((type) => {
                if (record instanceof type) {
                    excluded = true;
                }
            });
            if (excluded) {
                return;
            }
        }

        const anticipationResult = this.buildResult(record);

        record.results.push(anticipationResult);
    }

    protected buildResult(record: SpinRecord) {
        if (!record.layout) {
            return new AnticipationResult();
        }

        const anticipationResult = new AnticipationResult();
        this.reels = record.grid;
        anticipationResult.reelAnticipations = Array<boolean>(this.reels.length).fill(false);

        const scatters: SymbolDefinition[] = [];
        const lines: SymbolDefinition[] = [];
        const ways: SymbolDefinition[] = [];
        const located: SymbolDefinition[] = [];

        for (const sym of slotDefinition.symbolDefinitions) {
            switch (sym.matchType) {
                case SymbolMatchType.SCATTER: {
                    scatters.push(sym);
                    break;
                }
                case SymbolMatchType.LINES: {
                    lines.push(sym);
                    break;
                }
                case SymbolMatchType.WAYS: {
                    ways.push(sym);
                    break;
                }
                case SymbolMatchType.LOCATED: {
                    located.push(sym);
                    break;
                }
            }
        }

        this.evaluateScatters(scatters, anticipationResult, record);
        this.evaluateLines(lines, anticipationResult, record);
        this.evaluateWays(ways, anticipationResult, record);

        // TODO: Implement located wins if/when they are ever needed for a game.
        //this.evaluateLocatedWins(located, anticipationResult);

        return anticipationResult;
    }

    protected evaluateScatters(scatterSymbols: SymbolDefinition[], anticipationResult: AnticipationResult, record: SpinRecord) {
        if (scatterSymbols.length === 0) {
            return;
        }

        const idToTriggeredMap = new Map<number, number[]>();
        const indexToCountsMap = new Map<number, IScatterAnticipationTracker>();

        for (const sym of scatterSymbols) {
            indexToCountsMap.set(
                sym.index, 
                {
                    reelCounts: Array<number>(this.reels.length).fill(0),
                    requiredSymbolsCounts: Array<number>(this.reels.length).fill(0),
                    triggerCounts: sym.triggerCounts,
                    anticipation: sym.anticipation,
                    positions: [],
                    possibleMatchesPerReel: sym.possibleMatchesPerReel,
                    requiredSymbolIds: sym.requiredSymbolIds,
                    numRequiredSymbols: sym.numRequiredSymbols
                }
            );

            for (const eachId of sym.anticipationMatchIds) {
                if (idToTriggeredMap.get(eachId)) {
                    idToTriggeredMap.get(eachId).push(sym.index);
                } else {
                    idToTriggeredMap.set(eachId, [sym.index]);
                }
            }
        }

        for (let reel = 0; reel < this.reels.length; reel++) {
            for (let row = 0; row < this.reels[reel].length; row++) {
                const symIndex = slotDefinition.getSymbolDefinition(this.reels[reel][row]).index;

                if (idToTriggeredMap.get(symIndex)) {
                    for (const countedMatches of idToTriggeredMap.get(symIndex)) {
                        const countSym = indexToCountsMap.get(countedMatches);
                        countSym.reelCounts[reel]++;
                        countSym.positions.push(new Point(reel, row));

                        if (countSym.requiredSymbolIds?.some((id) => id === symIndex)) {
                            countSym.requiredSymbolsCounts[reel]++;
                        }
                    }
                }
            }
        }

        indexToCountsMap.forEach((value, key) => {
            for (const pos of value.positions) {
                if (!anticipationResult.positions.some((pt) => pt.x === pos.x && pt.y === pos.y)) {
                    anticipationResult.positions.push(pos);
                }
            }

            const amountRequired = value.numRequiredSymbols ?? 0;

            for (let i = 0; i < value.reelCounts.length; i++) {
                const trigCounts = value.triggerCounts.get(record.layout);
                const availableToPickUpStill = (value.reelCounts.length - i) * value.possibleMatchesPerReel;
                const sumUpTo = value.reelCounts.slice(0, i).reduce((acc, curr) => acc + curr, 0);
                const requiredSum = value.requiredSymbolsCounts.slice(0, i).reduce((acc, curr) => acc + curr, 0);
                const canWin = sumUpTo + availableToPickUpStill >= trigCounts[0];
                anticipationResult.reelAnticipations[i] = anticipationResult.reelAnticipations[i] || (requiredSum >= amountRequired && canWin && sumUpTo >= value.anticipation.get(record.layout) && sumUpTo <= trigCounts[trigCounts.length - 1]);
            }
        });
    }

    protected evaluateLines(lineSymbols: SymbolDefinition[], anticipationResult: AnticipationResult, record: SpinRecord) {
        if (lineSymbols.length === 0) {
            return;
        }

        const idToTriggeredMap = new Map<number, number[]>();
        const indexToCountsMap = new Map<number, ILineAnticipationTracker>();
        const paylines = gameiomModel.paylines;

        for (const sym of lineSymbols) {
            indexToCountsMap.set(
                sym.index, 
                {
                    lineCounts: [],
                    requiredSymbolsCounts: [],
                    triggerCounts: sym.triggerCounts,
                    anticipation: sym.anticipation,
                    positions: [],
                    possibleMatchesPerReel: sym.possibleMatchesPerReel,
                    requiredSymbolIds: sym.requiredSymbolIds,
                    numRequiredSymbols: sym.numRequiredSymbols
                }
            );

            for (const eachId of sym.anticipationMatchIds) {
                if (idToTriggeredMap.get(eachId)) {
                    idToTriggeredMap.get(eachId).push(sym.index);
                } else {
                    idToTriggeredMap.set(eachId, [sym.index]);
                }
            }
        }

        for (let lineIndex = 0; lineIndex < paylines.length; lineIndex++) {
            const line = paylines[lineIndex];

            for (let reel = 0; reel < line.length; reel++) {
                const row = line[reel];
                const symbolId = this.reels[reel][row];
                const symIndex = slotDefinition.getSymbolDefinition(symbolId).index;

                if (idToTriggeredMap.get(symIndex)) {
                    for (const countedMatches of idToTriggeredMap.get(symIndex)) {
                        const countSym = indexToCountsMap.get(countedMatches);
                        if (!countSym.lineCounts[lineIndex]) {
                            countSym.lineCounts[lineIndex] = Array<number>(this.reels.length).fill(0);
                        }

                        if (!countSym.requiredSymbolsCounts[lineIndex]) {
                            countSym.requiredSymbolsCounts[lineIndex] = Array<number>(this.reels.length).fill(0);
                        }
                        
                        countSym.lineCounts[lineIndex][reel] = 1;

                        if (countSym.requiredSymbolIds?.some((id) => id === symIndex)) {
                            countSym.requiredSymbolsCounts[lineIndex][reel] = 1;
                        }

                        if (!countSym.positions.some((pt) => pt.x === reel && pt.y === row)) {
                            countSym.positions.push(new Point(reel, row));
                        }
                    }
                } else {
                    break;
                }
            }
        }

        indexToCountsMap.forEach((value, key) => {
            for (const pos of value.positions) {
                if (!anticipationResult.positions.some((pt) => pt.x === pos.x && pt.y === pos.y)) {
                    anticipationResult.positions.push(pos);
                }
            }

            const trigCounts = value.triggerCounts.get(record.layout);

            for (let i = 0; i < value.lineCounts.length; i++) {
                if (!value.lineCounts[i]) {
                    continue;
                }

                let currentSum = 0;
                let requiredSum = 0;
                const amountRequired = value.numRequiredSymbols ?? 0;
                let canStillAccumulate = true;

                for (let j = 0; j < value.lineCounts[i].length; j++) {
                    const availableToPickUpStill = (value.lineCounts[i].length - j) * value.possibleMatchesPerReel;
                    const canWin = currentSum + availableToPickUpStill >= trigCounts[0];
                    anticipationResult.reelAnticipations[j] = anticipationResult.reelAnticipations[j] || (requiredSum >= amountRequired && canWin && currentSum >= value.anticipation.get(record.layout) && currentSum <= trigCounts[trigCounts.length - 1]);

                    const num = value.lineCounts[i][j] ?? 0;
                    const req = value.requiredSymbolsCounts[i][j] ?? 0;
                    requiredSum += req;

                    if (num === 0) {
                        canStillAccumulate = false;
                        currentSum = Number.NEGATIVE_INFINITY;
                    } else if (canStillAccumulate) {
                        currentSum += num;
                    }
                }
            }
        });
    }

    protected evaluateWays(waySymbols: SymbolDefinition[], anticipationResult: AnticipationResult, record: SpinRecord) {
        if (waySymbols.length === 0) {
            return;
        }

        const idToTriggeredMap = new Map<number, number[]>();
        const indexToCountsMap = new Map<number, IWayAnticipationTracker>();

        for (const sym of waySymbols) {
            indexToCountsMap.set(
                sym.index,
                {
                    reelCounts: Array<number>(this.reels.length).fill(0),
                    requiredSymbolsCounts: Array<number>(this.reels.length).fill(0),
                    triggerCounts: sym.triggerCounts,
                    anticipation: sym.anticipation,
                    positions: [],
                    possibleMatchesPerReel: sym.possibleMatchesPerReel,
                    requiredSymbolIds: sym.requiredSymbolIds,
                    numRequiredSymbols: sym.numRequiredSymbols
                }
            );

            for (const eachId of sym.anticipationMatchIds) {
                if (idToTriggeredMap.get(eachId)) {
                    idToTriggeredMap.get(eachId).push(sym.index);
                } else {
                    idToTriggeredMap.set(eachId, [sym.index]);
                }
            }
        }

        for (let reel = 0; reel < this.reels.length; reel++) {
            for (let row = 0; row < this.reels[reel].length; row++) {
                const symIndex = slotDefinition.getSymbolDefinition(this.reels[reel][row]).index;

                if (idToTriggeredMap.get(symIndex)) {
                    for (const countedMatches of idToTriggeredMap.get(symIndex)) {
                        const countSym = indexToCountsMap.get(countedMatches);
                        countSym.reelCounts[reel]++;
                        countSym.positions.push(new Point(reel, row));

                        if (countSym.requiredSymbolIds?.some((id) => id === symIndex)) {
                            countSym.requiredSymbolsCounts[reel]++;
                        }
                    }
                }
            }
        }

        indexToCountsMap.forEach((value, key) => {
            for (const pos of value.positions) {
                if (!anticipationResult.positions.some((pt) => pt.x === pos.x && pt.y === pos.y)) {
                    anticipationResult.positions.push(pos);
                }
            }

            const trigCounts = value.triggerCounts.get(record.layout);
            let currentSum = 0;
            let requiredSum = 0;
            const amountRequired = value.numRequiredSymbols ?? 0;
            let canStillAccumulate = true;

            for (let i = 0; i < value.reelCounts.length; i++) {
                const availableToPickUpStill = (value.reelCounts.length - i) * value.possibleMatchesPerReel;
                const canWin = currentSum + availableToPickUpStill >= trigCounts[0];
                anticipationResult.reelAnticipations[i] = anticipationResult.reelAnticipations[i] || (requiredSum >= amountRequired && canWin && currentSum >= value.anticipation.get(record.layout) && currentSum <= trigCounts[trigCounts.length - 1]);

                const num = value.reelCounts[i] ?? 0;
                const req = value.requiredSymbolsCounts[i] ?? 0;
                requiredSum += req;

                if (num === 0) {
                    canStillAccumulate = false;
                    currentSum = Number.NEGATIVE_INFINITY;
                } else if (canStillAccumulate) {
                    currentSum += num;
                }
            }
        });
    }
}